import { formatPeriodLabel } from '../constants/maintenance.constants';
import type {
	FertilizationPlanAdvice,
	MaintenanceFormData,
	MaintenanceParcellingType,
} from '../types';
import { monthNumberLabel, parseMonthNumber } from './parcellingCycle';

const NONE_VALUE = 'none';

export const getMaintenanceReferenceId = (value: unknown) => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	const reference = value as { _id?: unknown; id?: unknown };
	const id = reference._id ?? reference.id;
	if (typeof id === 'string') return id;
	if (id == null) return '';
	const asString = String(id);
	return asString && asString !== '[object Object]' ? asString : '';
};

export const getMaintenanceReferenceName = (
	value: unknown,
	fallback = ''
) => {
	if (value && typeof value === 'object') {
		const reference = value as { name?: unknown };
		if (typeof reference.name === 'string') return reference.name;
	}
	return fallback || (typeof value === 'string' ? value : '');
};

export const getMaintenanceSectorNames = (sectors: FertilizationPlanAdvice['sectors'] = []) =>
	sectors.map((sector) => getMaintenanceReferenceName(sector)).filter(Boolean);

export const normalizeMaintenanceMonth = (value?: string) => {
	if (!value) return '';
	const normalized = value.replace(/\//g, '-');
	return /^\d{4}-\d{2}/.test(normalized) ? normalized.slice(0, 7) : '';
};

export const normalizeMaintenanceStartingPeriod = (
	startingPeriod?: string,
	periods: string[] = []
) => normalizeMaintenanceMonth(startingPeriod || periods[0] || '');

export const getMaintenanceParcellingType = (
	advice: Pick<FertilizationPlanAdvice, 'parcellingType' | 'parcelling'>
): MaintenanceParcellingType | '' => {
	if (advice.parcellingType === 'fixed' || advice.parcellingType === 'dynamic') {
		return advice.parcellingType;
	}
	if (advice.parcelling && typeof advice.parcelling === 'object') {
		const type = (advice.parcelling as { typeParcelling?: unknown }).typeParcelling;
		if (type === 'fixed' || type === 'dynamic') return type;
	}
	return '';
};

export const mapMaintenanceAdviceToForm = (
	advice: FertilizationPlanAdvice
): MaintenanceFormData => ({
	name: advice.name ?? '',
	prop: getMaintenanceReferenceId(advice.prop),
	area: getMaintenanceReferenceId(advice.area),
	sectors: advice.sectors.map(getMaintenanceReferenceId).filter(Boolean),
	soilAnalysis: getMaintenanceReferenceId(advice.soilAnalysis),
	plantingAdvice: getMaintenanceReferenceId(advice.plantingAdvice) || NONE_VALUE,
	cropStage: advice.cropStage ?? '',
	productivity: advice.productivity == null ? '' : String(advice.productivity),
	parcellingType: getMaintenanceParcellingType(advice),
	parcelling: getMaintenanceReferenceId(advice.parcelling),
	cycleStartMonth:
		advice.cycleStartMonth != null
			? String(advice.cycleStartMonth)
			: String(parseMonthNumber(normalizeMaintenanceStartingPeriod(advice.startingPeriod, advice.periods)) ?? ''),
	floweringMonth:
		normalizeMaintenanceMonth(advice.floweringMonth) ||
		normalizeMaintenanceMonth(advice.floweringDate) ||
		'',
});

export const getMaintenanceCycleStartLabel = (
	advice: Pick<FertilizationPlanAdvice, 'cycleStartMonth' | 'startingPeriod' | 'periods'>
) =>
	monthNumberLabel(advice.cycleStartMonth) ||
	formatPeriodLabel(normalizeMaintenanceStartingPeriod(advice.startingPeriod, advice.periods));

export const getMaintenancePreviewStart = (
	advice?: Pick<FertilizationPlanAdvice, 'cycleStartMonth' | 'startingPeriod' | 'periods'> | null
) =>
	advice?.cycleStartMonth ??
	parseMonthNumber(normalizeMaintenanceStartingPeriod(advice?.startingPeriod, advice?.periods));
